import { useEffect, useRef, useState, useCallback } from "react";
import { getAuth, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setConnectionStatus,
  setReconnectCountdown,
  selectSequenceNumber
} from '@/store/slices/websocketSlice';
import { selectCurrentGame } from '@/store/slices/gameSessionSlice';

const CLOUD_RUN_WS_BASE_URL = process.env.REACT_APP_CLOUD_RUN_WS_URL;

/**
 * Redux-integrated WebSocket hook
 * Dispatches messageReceived actions to Redux store instead of using callbacks
 * The websocketMiddleware handles message ordering and routing
 */
function useAppSocketRedux(user: FirebaseUser | null): { isConnected: boolean; reconnectCountdown: number } {
    const dispatch = useAppDispatch();
    const activeGame = useAppSelector(selectCurrentGame);
    const expectedSequenceForActiveGame = useAppSelector((state) =>
      activeGame ? selectSequenceNumber(state, `game:${activeGame.gameId}`) : 1
    );
    const activeGameRef = useRef(activeGame);
    const expectedSequenceRef = useRef(expectedSequenceForActiveGame);
    const [isConnected, setIsConnected] = useState(false);
    const [reconnectCountdown, setLocalReconnectCountdown] = useState(0);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isMounted = useRef(true);
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const tokenRefreshInterval = useRef<NodeJS.Timeout | null>(null);
    const currentToken = useRef<string | null>(null);
    const hasConnectedOnceRef = useRef(false);
    const PING_INTERVAL_MS = 30000;
    const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

    // --- Ping/Pong Logic ---
    const startPing = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({ type: "ping" }));
            }
        }, PING_INTERVAL_MS);
    }, []);

    const stopPing = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
    }, []);

    // Token refresh mechanism
    const startTokenRefresh = useCallback((user: FirebaseUser) => {
        if (tokenRefreshInterval.current) {
            clearInterval(tokenRefreshInterval.current);
        }
        tokenRefreshInterval.current = setInterval(async () => {
            try {
                const newToken = await user.getIdToken(true);
                currentToken.current = newToken;
            } catch (error) {
                console.error("❌ Failed to refresh Firebase token:", error);
            }
        }, TOKEN_REFRESH_INTERVAL_MS);
    }, [TOKEN_REFRESH_INTERVAL_MS]);

    const stopTokenRefresh = useCallback(() => {
        if (tokenRefreshInterval.current) {
            clearInterval(tokenRefreshInterval.current);
            tokenRefreshInterval.current = null;
        }
    }, []);

    // Fetch game snapshot on reconnection to recover missed messages
    const fetchGameSnapshot = useCallback(async (gameId: number, tournamentId: number, sinceSeq: number) => {
        try {
            const baseUrl = CLOUD_RUN_WS_BASE_URL?.replace('wss://', 'https://').replace('ws://', 'http://');
            const response = await fetch(
                `${baseUrl}/api/game/snapshot?gameId=${gameId}&tournamentId=${tournamentId}&since=${sinceSeq}`
            );

            if (!response.ok) {
                throw new Error(`Snapshot fetch failed: ${response.statusText}`);
            }

            const snapshot = await response.json();
            // Dispatch recovered moves to Redux
            if (snapshot.moves && snapshot.moves.length > 0) {
                snapshot.moves.forEach((move: string, index: number) => {
                    const from = move.slice(0, 2);
                    const to = move.slice(2, 4);
                    const promotion = move.length > 4 ? move[4] : undefined;
                    dispatch({
                        type: 'websocket/messageReceived',
                        payload: {
                            type: 'move',
                            sequence: sinceSeq + index + 1,
                            data: {
                                gameId,
                                moveNotation: move,
                                from,
                                to,
                                promotion,
                            }
                        }
                    });
                });
            }

            // Update clock times if available
            if (snapshot.clock) {
                dispatch({
                    type: 'websocket/messageReceived',
                    payload: {
                        type: 'clock_tick',
                        data: {
                            gameId,
                            whiteTimeMs: snapshot.clock.whiteTimeMs,
                            blackTimeMs: snapshot.clock.blackTimeMs,
                            activePlayer: snapshot.clock.activePlayer,
                            clockPhase: snapshot.clock.clockPhase,
                        }
                    }
                });
            }

            return snapshot;
        } catch (error) {
            console.error(`❌ [Snapshot Recovery] Failed to fetch snapshot for game ${gameId}:`, error);
            return null;
        }
    }, [dispatch]);

    useEffect(() => {
      activeGameRef.current = activeGame;
      expectedSequenceRef.current = expectedSequenceForActiveGame;
    }, [activeGame, expectedSequenceForActiveGame]);

    useEffect(() => {
        isMounted.current = true;
        let retryCount = 0;

        const connect = async () => {
            if (!user || !isMounted.current) {
                setIsConnected(false);
                dispatch(setConnectionStatus('disconnected'));
                return;
            }

            if (!CLOUD_RUN_WS_BASE_URL) {
              console.error("Critical: REACT_APP_CLOUD_RUN_WS_URL environment variable is not defined!");
              setIsConnected(false);
              dispatch(setConnectionStatus('disconnected'));
              return;
            }

            let idToken: string;
            try {
                if (currentToken.current) {
                    idToken = currentToken.current;
                } else {
                    idToken = await user.getIdToken(true);
                    currentToken.current = idToken;
                }
            } catch (error) {
                console.error("Failed to get Firebase ID Token:", error);
                setIsConnected(false);
                dispatch(setConnectionStatus('disconnected'));
                return;
            }

           const wsUrl = `${CLOUD_RUN_WS_BASE_URL}/api/ws?firebase_token=${idToken}`;

            const socket = new WebSocket(wsUrl);
            socketRef.current = socket;

            socket.onopen = async () => {
                const isReconnect = hasConnectedOnceRef.current;
                hasConnectedOnceRef.current = true;
                retryCount = 0;
                setIsConnected(true);
                dispatch(setConnectionStatus('connected'));
                startPing();
                startTokenRefresh(user);

                // RECONNECTION RECOVERY: pull missed events for active game stream.
                const latestGame = activeGameRef.current;
                if (isReconnect && latestGame) {
                    const sinceSeq = Math.max(0, expectedSequenceRef.current - 1);
                    await fetchGameSnapshot(latestGame.gameId, latestGame.tournamentId, sinceSeq);
                }
            };

            socket.onmessage = (msg) => {
                try {
                    const data = JSON.parse(msg.data);

                    // Dispatch to Redux - middleware will handle ordering and routing
                    dispatch({ type: 'websocket/messageReceived', payload: data });
                } catch (e) {
                    console.error("WebSocket parse error:", e);
                }
            };

            socket.onerror = (err) => {
                console.error("WebSocket error", err);
            };

            socket.onclose = (e) => {
                setIsConnected(false);
                dispatch(setConnectionStatus('disconnected'));
                stopPing();
                stopTokenRefresh();
                // Reconnect loop handles expected transient disconnects.

                if (!isMounted.current) return;

                dispatch(setConnectionStatus('reconnecting'));

                const delay = Math.min(10000, 1000 * 2 ** retryCount);
                const delaySeconds = Math.ceil(delay / 1000);

                // Show countdown to user
                setLocalReconnectCountdown(delaySeconds);
                dispatch(setReconnectCountdown(delaySeconds));

                // Clear any existing countdown interval
                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                }

                // Update countdown every second
                countdownIntervalRef.current = setInterval(() => {
                    setLocalReconnectCountdown(prev => {
                        const next = prev - 1;
                        if (next <= 0) {
                            if (countdownIntervalRef.current) {
                                clearInterval(countdownIntervalRef.current);
                                countdownIntervalRef.current = null;
                            }
                        }
                        dispatch(setReconnectCountdown(Math.max(0, next)));
                        return Math.max(0, next);
                    });
                }, 1000);

                reconnectTimeout.current = setTimeout(() => {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                    }
                    retryCount++;
                    currentToken.current = null;
                    connect();
                }, delay);
            };
        };

        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                connect();
            } else {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.close(1000, "User logged out");
                }
                setIsConnected(false);
                dispatch(setConnectionStatus('disconnected'));
                stopPing();
            }
        });

        return () => {
            isMounted.current = false;
            unsubscribe();

            if (reconnectTimeout.current) {
                clearTimeout(reconnectTimeout.current);
            }

            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }

            if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.close(1000, "Component unmounted");
            }
            stopPing();
            stopTokenRefresh();
            currentToken.current = null;
        };
    }, [
      user,
      dispatch,
      startPing,
      stopPing,
      startTokenRefresh,
      stopTokenRefresh,
      fetchGameSnapshot
    ]);

    return { isConnected, reconnectCountdown };
}

export default useAppSocketRedux;
